import {
	ChevronLeft,
	ChevronRight,
	FileText,
	Minimize,
	RotateCcw,
	ZoomIn,
	ZoomOut,
} from 'lucide-react'
import * as React from 'react'
import { getDownloadUrl } from '@/client/sdk.gen'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactMetadataBlock } from '@/components/project/artifact-viewer/ArtifactMetadataBlock'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { extractErrorMessage } from '@/utils/error'
import './PdfViewer.css'

type ReactPdfComponents = Pick<typeof import('react-pdf'), 'Document' | 'Page'>

export interface PdfViewerProps {
	artifact: ArtifactResponse
	activeTab: 'preview' | 'metadata'
}

interface PendingZoomScroll {
	mouseX: number
	mouseY: number
	cursorInPageX: number
	scrollTopAtEvent: number
	oldScale: number
	newScale: number
	pageWidthSnapshot: number
}

export function PdfViewer({ artifact, activeTab }: PdfViewerProps) {
	const { t } = useTranslation()
	const [pdfRuntimeError, setPdfRuntimeError] = React.useState<string | null>(null)
	const [pdfComponents, setPdfComponents] = React.useState<ReactPdfComponents | null>(null)
	const containerRef = React.useRef<HTMLDivElement | null>(null)
	const pageRefs = React.useRef<Map<number, HTMLDivElement>>(new Map())
	const pendingZoomScrollRef = React.useRef<PendingZoomScroll | null>(null)
	const zoomScaleRef = React.useRef(1)
	const pageWidthRef = React.useRef(640)
	const pageAspectRatioRef = React.useRef<number | null>(null)
	const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)
	const [previewLoading, setPreviewLoading] = React.useState(false)
	const [previewError, setPreviewError] = React.useState<string | null>(null)
	/** Incremented on Retry to refetch presigned URL and remount react-pdf Document. */
	const [previewLoadGeneration, setPreviewLoadGeneration] = React.useState(0)
	const [currentPage, setCurrentPage] = React.useState(1)
	const [totalPages, setTotalPages] = React.useState(0)
	const [pageWidth, setPageWidth] = React.useState(640)
	const [zoomScale, setZoomScale] = React.useState(1)
	const [pageAspectRatio, setPageAspectRatio] = React.useState<number | null>(null)

	const ZOOM_MIN = 0.5
	const ZOOM_MAX = 2
	const ZOOM_STEP = 0.25
	const scaledWidth = Math.round(pageWidth * zoomScale)

	zoomScaleRef.current = zoomScale
	pageWidthRef.current = pageWidth
	pageAspectRatioRef.current = pageAspectRatio

	React.useEffect(() => {
		let cancelled = false
		void Promise.all([import('react-pdf'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')])
			.then(([m, workerMod]) => {
				if (cancelled) return
				m.pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default as string
				setPdfComponents({ Document: m.Document, Page: m.Page })
			})
			.catch(() => {
				if (cancelled) return
				setPdfRuntimeError(t('pages.projectDetail.artifactPreview.errorLoading'))
			})
		return () => {
			cancelled = true
		}
	}, [t])

	React.useEffect(() => {
		const element = containerRef.current
		if (!element) return

		let timeoutId: ReturnType<typeof setTimeout> | null = null

		const SCROLLBAR_WIDTH_THRESHOLD = 20

		const applyWidth = () => {
			const nextWidth = Math.floor(element.clientWidth - 24)
			const clamped = nextWidth > 280 ? nextWidth : 280
			setPageWidth((prev) => {
				if (prev === clamped) return prev
				if (Math.abs(clamped - prev) <= SCROLLBAR_WIDTH_THRESHOLD) return prev
				return clamped
			})
		}

		applyWidth()

		const onResize = () => {
			if (timeoutId) clearTimeout(timeoutId)
			timeoutId = setTimeout(applyWidth, 120)
		}

		const observer = new ResizeObserver(onResize)
		observer.observe(element)

		return () => {
			if (timeoutId) clearTimeout(timeoutId)
			observer.disconnect()
		}
	}, [])

	// biome-ignore lint/correctness/useExhaustiveDependencies: previewLoadGeneration bumps on Retry to refetch presigned URL
	React.useEffect(() => {
		setPreviewUrl(null)
		setPreviewError(null)
		setPreviewLoading(false)
		setCurrentPage(1)
		setTotalPages(0)
		pageRefs.current.clear()
		setZoomScale(1)
		setPageAspectRatio(null)

		if (artifact.status !== 'completed') return

		let cancelled = false

		const fetchPreviewUrl = async () => {
			setPreviewLoading(true)
			try {
				const response = await getDownloadUrl({
					path: { project_id: artifact.project_id, artifact_id: artifact.id },
				})

				if (cancelled) return

				if (response.error) {
					setPreviewError(
						extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorLoading'))
					)
					return
				}

				if (!response.data?.download_url) {
					setPreviewError(t('pages.projectDetail.artifactPreview.errorLoading'))
					return
				}

				setPreviewUrl(response.data.download_url)
			} catch (error) {
				if (cancelled) return
				setPreviewError(
					extractErrorMessage(error, t('pages.projectDetail.artifactPreview.errorLoading'))
				)
			} finally {
				if (!cancelled) {
					setPreviewLoading(false)
				}
			}
		}

		fetchPreviewUrl().catch((error) => {
			if (cancelled) return
			setPreviewError(
				extractErrorMessage(error, t('pages.projectDetail.artifactPreview.errorLoading'))
			)
			setPreviewLoading(false)
		})

		return () => {
			cancelled = true
		}
	}, [artifact.id, artifact.project_id, artifact.status, previewLoadGeneration, t])

	const handlePreviewRetry = React.useCallback(() => {
		setPreviewError(null)
		setTotalPages(0)
		setCurrentPage(1)
		pageRefs.current.clear()
		setPreviewLoadGeneration((g) => g + 1)
	}, [])

	const scrollToPage = React.useCallback((pageNum: number) => {
		const container = containerRef.current
		const pageEl = pageRefs.current.get(pageNum)
		if (!container || !pageEl) return
		const containerRect = container.getBoundingClientRect()
		const pageRect = pageEl.getBoundingClientRect()
		const targetScrollTop = container.scrollTop + pageRect.top - containerRect.top - 12
		container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' })
	}, [])

	const goToPreviousPage = React.useCallback(() => {
		setCurrentPage((page) => {
			const prev = Math.max(1, page - 1)
			scrollToPage(prev)
			return prev
		})
	}, [scrollToPage])

	const goToNextPage = React.useCallback(() => {
		setCurrentPage((page) => {
			const next = totalPages > 0 ? Math.min(totalPages, page + 1) : page
			scrollToPage(next)
			return next
		})
	}, [totalPages, scrollToPage])

	const zoomIn = React.useCallback(() => {
		setZoomScale((s) => Math.min(ZOOM_MAX, s + ZOOM_STEP))
	}, [])
	const zoomOut = React.useCallback(() => {
		setZoomScale((s) => Math.max(ZOOM_MIN, s - ZOOM_STEP))
	}, [])

	const fitToViewport = React.useCallback(() => {
		const el = containerRef.current
		if (!el || pageAspectRatio == null || pageWidth <= 0) return
		const scrollbarGutter = 16
		const padding = 24
		const clientW = el.clientWidth
		const clientH = el.clientHeight
		const w = Math.max(280, clientW - padding - scrollbarGutter)
		const h = Math.max(200, clientH - padding)
		if (w <= 0 || h <= 0) return
		const fitScale = Math.min(w, h / pageAspectRatio) / pageWidth
		const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, fitScale))
		setZoomScale(newScale)
	}, [pageAspectRatio, pageWidth])

	const handleDocumentLoadSuccess = React.useCallback(
		(pdf: {
			numPages: number
			getPage: (
				n: number
			) => Promise<{ getViewport: (opts: { scale: number }) => { width: number; height: number } }>
		}) => {
			setTotalPages(pdf.numPages)
			setCurrentPage((page) => Math.min(page, pdf.numPages))
			setPreviewError(null)
			pdf
				.getPage(1)
				.then((page) => {
					const viewport = page.getViewport({ scale: 1 })
					setPageAspectRatio(viewport.height / viewport.width)
				})
				.catch(() => setPageAspectRatio(null))
		},
		[]
	)

	const handleDocumentLoadError = React.useCallback(
		(error: unknown) => {
			setPreviewError(
				extractErrorMessage(error, t('pages.projectDetail.artifactPreview.errorLoading'))
			)
		},
		[t]
	)

	const previewFailureMessage = previewError ?? pdfRuntimeError

	const hasPdfReady =
		artifact.status === 'completed' && Boolean(previewUrl) && !previewFailureMessage

	// Track current page via scroll position
	React.useEffect(() => {
		const el = containerRef.current
		if (!el || !hasPdfReady || totalPages === 0) return

		const handleScroll = () => {
			const containerRect = el.getBoundingClientRect()
			const viewportMidY = containerRect.top + el.clientHeight / 2
			let closestPage = 1
			let closestDist = Number.POSITIVE_INFINITY
			for (const [pageNum, pageEl] of pageRefs.current) {
				const rect = pageEl.getBoundingClientRect()
				const dist = Math.abs(rect.top + rect.height / 2 - viewportMidY)
				if (dist < closestDist) {
					closestDist = dist
					closestPage = pageNum
				}
			}
			setCurrentPage(closestPage)
		}

		el.addEventListener('scroll', handleScroll, { passive: true })
		return () => el.removeEventListener('scroll', handleScroll)
	}, [hasPdfReady, totalPages])

	React.useEffect(() => {
		const el = containerRef.current
		if (!el || !hasPdfReady) return

		// Container has p-3 (12px) padding on all sides; page is centered within padded area
		const PADDING = 12

		const handleWheel = (e: WheelEvent) => {
			if (!e.ctrlKey && !e.metaKey) return
			e.preventDefault()

			const rect = el.getBoundingClientRect()
			const mouseX = e.clientX - rect.left
			const mouseY = e.clientY - rect.top

			const oldScale = zoomScaleRef.current
			const pw = pageWidthRef.current

			const delta = -e.deltaY * 0.002
			const newScale = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, oldScale + delta))
			zoomScaleRef.current = newScale

			const oldScaledWidth = Math.round(pw * oldScale)
			// Horizontal: cursor position relative to the centered page
			const oldPageLeft = PADDING + Math.max(0, (el.clientWidth - 24 - oldScaledWidth) / 2)
			const cursorInPageX = el.scrollLeft + mouseX - oldPageLeft

			pendingZoomScrollRef.current = {
				mouseX,
				mouseY,
				cursorInPageX,
				scrollTopAtEvent: el.scrollTop,
				oldScale,
				newScale,
				pageWidthSnapshot: pw,
			}

			setZoomScale(newScale)
		}

		el.addEventListener('wheel', handleWheel, { passive: false })
		return () => el.removeEventListener('wheel', handleWheel)
	}, [hasPdfReady])

	React.useEffect(() => {
		const pending = pendingZoomScrollRef.current
		if (!pending) return
		const el = containerRef.current
		if (!el) return
		pendingZoomScrollRef.current = null

		const PADDING = 12

		const rafId = requestAnimationFrame(() => {
			const {
				mouseX,
				mouseY,
				cursorInPageX,
				scrollTopAtEvent,
				oldScale,
				newScale,
				pageWidthSnapshot: pw,
			} = pending
			const scaleFactor = newScale / oldScale

			// Horizontal: exact formula using page centering geometry
			const newScaledWidth = Math.round(pw * newScale)
			const newPageLeft = PADDING + Math.max(0, (el.clientWidth - 24 - newScaledWidth) / 2)
			const newScrollLeft = newPageLeft + cursorInPageX * scaleFactor - mouseX

			// Vertical: scale the scroll position directly (pages stack top-to-bottom, no centering)
			const newScrollTop = (scrollTopAtEvent + mouseY) * scaleFactor - mouseY

			el.scrollLeft = Math.max(0, Math.min(newScrollLeft, el.scrollWidth - el.clientWidth))
			el.scrollTop = Math.max(0, Math.min(newScrollTop, el.scrollHeight - el.clientHeight))
		})
		return () => cancelAnimationFrame(rafId)
	}, [])

	const PdfDocument = pdfComponents?.Document
	const PdfPage = pdfComponents?.Page

	return (
		<div className="flex h-full min-h-0 flex-col">
			{activeTab === 'preview' && (
				<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4">
					<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
						<div className="flex items-center gap-2">
							<Button
								type="button"
								variant="outline"
								size="icon-sm"
								onClick={goToPreviousPage}
								disabled={!hasPdfReady || currentPage <= 1}
								aria-label={t('common.previous')}
							>
								<ChevronLeft className="size-4" aria-hidden />
							</Button>
							<span className="min-w-14 text-center text-sm text-muted-foreground tabular-nums">
								{hasPdfReady ? `${currentPage} / ${totalPages}` : '— / —'}
							</span>
							<Button
								type="button"
								variant="outline"
								size="icon-sm"
								onClick={goToNextPage}
								disabled={!hasPdfReady || currentPage >= totalPages}
								aria-label={t('common.next')}
							>
								<ChevronRight className="size-4" aria-hidden />
							</Button>
							<div className="ml-2 h-6 w-px bg-border" aria-hidden />
							<Button
								type="button"
								variant="outline"
								size="icon-sm"
								onClick={zoomOut}
								disabled={!hasPdfReady || zoomScale <= ZOOM_MIN}
								aria-label={t('pages.projectDetail.artifactPreview.zoomOut')}
							>
								<ZoomOut className="size-4" aria-hidden />
							</Button>
							<span className="min-w-12 text-center text-sm text-muted-foreground tabular-nums">
								{hasPdfReady ? `${Math.round(zoomScale * 100)}%` : '—'}
							</span>
							<Button
								type="button"
								variant="outline"
								size="icon-sm"
								onClick={zoomIn}
								disabled={!hasPdfReady || zoomScale >= ZOOM_MAX}
								aria-label={t('pages.projectDetail.artifactPreview.zoomIn')}
							>
								<ZoomIn className="size-4" aria-hidden />
							</Button>
							<Button
								type="button"
								variant="outline"
								size="icon-sm"
								onClick={fitToViewport}
								disabled={!hasPdfReady || pageAspectRatio == null}
								aria-label={t('pages.projectDetail.artifactPreview.fitToViewport')}
							>
								<Minimize className="size-4" aria-hidden />
							</Button>
						</div>
					</div>

					<div
						ref={containerRef}
						className="relative min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-muted/20"
						role="application"
						aria-label={t('pages.projectDetail.artifactPreview.tabPreview')}
					>
						{artifact.status !== 'completed' ? (
							<div className="flex min-h-full flex-col items-center justify-center p-3 text-center">
								<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
								<p className="mt-3 text-sm font-medium text-foreground">
									{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
								</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{t('pages.projectDetail.artifactPreview.previewPending')}
								</p>
							</div>
						) : previewLoading ? (
							<div className="flex min-h-full items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
								<Spinner className="size-4" aria-hidden />
								{t('common.loading')}
							</div>
						) : previewFailureMessage ? (
							<div className="flex min-h-full items-center justify-center p-4">
								<div className="w-full max-w-md rounded-lg border border-[#ff3131]/30 bg-[#ff3131]/10 p-4">
									<p className="text-sm font-semibold text-[#ff3131]">
										{t('pages.projectDetail.artifactPreview.errorLoading')}
									</p>
									<p className="mt-1 text-sm text-red-700 dark:text-red-300">{previewFailureMessage}</p>
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="mt-4 border-destructive/50 text-destructive hover:bg-destructive/10"
										onClick={handlePreviewRetry}
									>
										<RotateCcw className="size-3.5 mr-1.5" aria-hidden />
										{t('pages.projectDetail.artifactPreview.retry')}
									</Button>
								</div>
							</div>
						) : previewUrl && (!PdfDocument || !PdfPage) ? (
							<div className="flex min-h-full items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
								<Spinner className="size-4" aria-hidden />
								{t('common.loading')}
							</div>
						) : previewUrl && PdfDocument && PdfPage ? (
							<PdfDocument
								key={`${previewLoadGeneration}-${previewUrl}`}
								file={previewUrl}
								suspense={false}
								loading={
									<div className="flex min-h-full items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
										<Spinner className="size-4" aria-hidden />
										{t('common.loading')}
									</div>
								}
								onLoadSuccess={handleDocumentLoadSuccess}
								onLoadError={handleDocumentLoadError}
								className="flex min-w-full flex-col items-center gap-4 p-3 w-max"
							>
								{Array.from({ length: totalPages }, (_, i) => {
									const pageNum = i + 1
									return (
										<div
											key={pageNum}
											ref={(el) => {
												if (el) pageRefs.current.set(pageNum, el)
												else pageRefs.current.delete(pageNum)
											}}
										>
											<PdfPage
												pageNumber={pageNum}
												width={scaledWidth}
												className="overflow-hidden rounded-md shadow-sm"
											/>
										</div>
									)
								})}
							</PdfDocument>
						) : (
							<div className="flex min-h-full flex-col items-center justify-center p-3 text-center">
								<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
								<p className="mt-3 text-sm font-medium text-foreground">
									{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
								</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{t('pages.projectDetail.artifactPreview.previewNotAvailableDescription')}
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			{activeTab === 'metadata' && (
				<div className="space-y-6 p-4">
					<ArtifactMetadataBlock artifact={artifact} />
				</div>
			)}
		</div>
	)
}
